/**
 * SendToSlackForm Component
 *
 * Form for sending reports to Slack channels.
 * Features:
 * - Slack credential selection
 * - Channel list with invite status
 * - Invite to channel functionality
 * - Multi-channel selection
 * - Optional message
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Input, TextArea, Label, ScrollArea, Button } from '@strivelabs/ui-core';
import { Loader2, CheckCircle, UserPlus, X, Search } from 'lucide-react';
import { executeAppAction } from '@/services/DataService';
import { BASE_URL } from '@/services/DataService';
import { CredentialPicker } from '@/components/agent/CredentialPicker';
import { useCredentials } from '@/hooks/useConnections';
import { ConnectionsCardData } from '@/types/connections';
import { useGeneratePdfFromBlocks } from '@/hooks/useGeneratePdfFromBlocks';
import { toPdfFilename } from '@/utils/pdf';
import type { UIBlock, ContainerBlock, InsightCardBlock } from '@/types/ui-blocks';

interface SlackChannel {
  id: string;
  name: string;
  is_member: boolean;
}

export interface SendToSlackFormProps {
  /** Insight preview blocks from behaviour action */
  blocks: (UIBlock | ContainerBlock | InsightCardBlock)[];
  /** Default title */
  title?: string;
  /** Callback fired when the overlay is closed */
  onClose: () => void;
  /** Callback fired when the form is successfully submitted */
  onSuccess: () => void;
}

export const SendToSlackForm: React.FC<SendToSlackFormProps> = ({
  blocks,
  title,
  onClose,
  onSuccess,
}) => {
  const [selectedCredential, setSelectedCredential] = useState<string>('');
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [reportTitle, setReportTitle] = useState<string>(title || 'Report');
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [invitingChannels, setInvitingChannels] = useState<Record<string, boolean>>({});
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { generatePdf } = useGeneratePdfFromBlocks();

  const { data: credentialsData } = useCredentials();

  useEffect(() => {
    if (credentialsData?.credentials && !selectedCredential) {
      const slackCredentials = credentialsData.credentials.filter(
        (cred: ConnectionsCardData) => cred.app_name === 'slack'
      );
      if (slackCredentials.length > 0) {
        setSelectedCredential(slackCredentials[0].id);
      }
    }
  }, [credentialsData, selectedCredential]);

  const fetchChannels = useCallback(async () => {
    setIsLoadingChannels(true);
    try {
      const response = await executeAppAction(
        BASE_URL,
        'slack',
        'list_conversations',
        [{ id: selectedCredential }],
        [{}]
      );

      if (response?.items?.[0]?.data?.channels) {
        const channelList = response.items[0].data.channels.map((ch: { id: string; name: string; is_member: boolean }) => ({
          id: ch.id,
          name: ch.name || ch.id,
          is_member: ch.is_member ?? false,
        }));
        setChannels(channelList);
      }
    } catch (error) {
      console.error('Error fetching Slack channels:', error);
      toast({
        title: 'Failed to fetch Slack channels',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingChannels(false);
    }
  }, [selectedCredential]);

  useEffect(() => {
    if (selectedCredential) {
      fetchChannels();
    } else {
      setChannels([]);
      setSelectedChannel([]);
    }
  }, [selectedCredential, fetchChannels]);

  const handleInviteToChannel = async (channelId: string) => {
    setInvitingChannels((prev) => ({ ...prev, [channelId]: true }));
    try {
      await executeAppAction(
        BASE_URL,
        'slack',
        'join_conversations',
        [{ id: selectedCredential }],
        [{ channels: [channelId] }]
      );

      setChannels((prev) =>
        prev.map((ch) =>
          ch.id === channelId ? { ...ch, is_member: true } : ch
        )
      );

      toast({
        title: 'Successfully joined channel',
        variant: 'default',
      });
    } catch (error) {
      console.error('Error joining channel:', error);
      toast({
        title: 'Failed to join channel',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setInvitingChannels((prev) => ({ ...prev, [channelId]: false }));
    }
  };

  const handleSendToSlack = async () => {
    if (selectedChannel.length === 0) {
      toast({
        title: 'Please select at least one channel',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const pdfResult = await generatePdf({
        blocks,
        title: reportTitle
      });

      const pdfResponse = await fetch(pdfResult.url);

      if (!pdfResponse.ok) {
        throw new Error('Failed to fetch generated PDF');
      }

      // Convert PDF to base64 for upload using FileReader API
      const pdfBlob = await pdfResponse.blob();
      const pdfBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          resolve(base64data.split(',')[1]);
        };
        reader.onerror = (error) => reject(error);
      });

      await executeAppAction(
        BASE_URL,
        'slack',
        'upload_file_to_channel',
        [{ id: selectedCredential }],
        undefined,
        [{
          data: {
            channels: selectedChannel,
            initial_comment: message || undefined,
            title: reportTitle,
          },
          binaries: {
            file: {
              content_base_64: pdfBase64,
              file_name: toPdfFilename(reportTitle),
              mime_type: 'application/pdf',
            },
          },
        }]
      );

      toast({
        title: selectedChannel.length > 1
          ? `Successfully sent to ${selectedChannel.length} channels`
          : 'Successfully sent to Slack',
        variant: 'default',
      });

      onSuccess();
    } catch (error) {
      console.error('Error sending to Slack:', error);
      toast({
        title: 'Failed to send to Slack',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Filter channels based on search query
  const filteredChannels = React.useMemo(() =>
    channels.filter((channel) =>
      channel.name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  [channels, searchQuery]);

  // Sort channels: invited first, then by name
  const sortedChannels = React.useMemo(() =>
    [...filteredChannels].sort((a, b) => {
      if (a.is_member && !b.is_member) return -1;
      if (!a.is_member && b.is_member) return 1;
      return (a.name || '').localeCompare(b.name || '');
    }),
  [filteredChannels]);

  const canSend = React.useMemo(() => {
    if (!selectedCredential || selectedChannel.length === 0) return false;
    // Check that all selected channels are ones we're a member of
    return selectedChannel.every(channelId => {
      const channel = channels.find(ch => ch.id === channelId);
      return channel?.is_member;
    });
  }, [selectedCredential, selectedChannel, channels]);

  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-black/50 backdrop-blur-[2px]">
      {/* Close Button - top right corner */}
      <Button
        type="button"
        onClick={onClose}
        variant="ghost"
        size="icon"
        className="absolute right-6 top-6 z-20 rounded-full text-white/60 hover:bg-white/10 hover:text-white"
        aria-label="Close"
      >
        <X size={24} />
      </Button>

      {/* Gradient Overlay - positioned at top, allows content behind to show through */}
      <div
        className="absolute left-1/2 top-0 h-[1744px] w-[1601px] -translate-x-1/2 opacity-90"
        style={{
          background: 'var(--gradient-overlay)',
          filter: 'blur(137.65px)',
          borderRadius: '701px',
        }}
      />

      {/* Form - centered with Flexbox, scrollable */}
      <div
        className="relative z-10 w-full max-w-[640px] px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center space-y-6">
          {/* Title */}
          <h1 className="font-fraunces-display text-center text-[40px] font-normal leading-[44px] tracking-[-0.8px] text-white">
            Send to Slack
          </h1>

          {/* Subtitle */}
          <p className="max-w-[360px] text-center text-sm leading-6 tracking-[-0.28px] text-white/60">
            Share this report with your team on Slack
          </p>

          {/* Form Fields - centered with consistent spacing */}
          <div className="flex w-full max-w-[480px] flex-col gap-4 pt-4">
            {/* Slack Workspace */}
            <div className="space-y-2">
              <Label className="text-sm text-white">Slack Workspace</Label>
              <CredentialPicker
                appName="slack"
                value={selectedCredential}
                onValueChange={setSelectedCredential}
                className="h-auto w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] text-white backdrop-blur-sm hover:bg-white/10"
              />
            </div>

            {/* Report Title */}
            <div className="space-y-2">
              <Label className="text-sm text-white">Report Title</Label>
              <Input
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Enter report title"
                className="h-auto w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 pb-[9px] pt-[8px] text-sm text-white placeholder:text-white/40 backdrop-blur-sm hover:bg-white/10 focus-visible:ring-white/20"
              />
            </div>

            {/* Channel Selection */}
            {selectedCredential && (
              <div className="space-y-2">
                <Label className="text-sm text-white">Select Channels</Label>
                {isLoadingChannels ? (
                  <div className="flex items-center justify-center py-8 rounded-md border-[0.5px] border-white/10 bg-white/[0.08]">
                    <Loader2 className="w-6 h-6 animate-spin text-white/60" />
                  </div>
                ) : channels.length > 0 ? (
                  <div className="space-y-2">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search channels..."
                        className="h-auto w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] pl-9 pr-3 pb-[9px] pt-[8px] text-sm text-white placeholder:text-white/40 backdrop-blur-sm hover:bg-white/10 focus-visible:ring-white/20"
                      />
                    </div>

                    {/* Channel List */}
                    <ScrollArea className="h-[220px] rounded-md border-[0.5px] border-white/10 bg-white/[0.08] backdrop-blur-sm">
                      <div className="p-2">
                        {sortedChannels.length > 0 ? (
                          sortedChannels.map((channel) => {
                            const isInviting = invitingChannels[channel.id];
                            const isSelected = selectedChannel.includes(channel.id);

                            const handleToggleChannel = () => {
                              if (!channel.is_member) return;

                              setSelectedChannel(prev =>
                                prev.includes(channel.id)
                                  ? prev.filter(id => id !== channel.id)
                                  : [...prev, channel.id]
                              );
                            };

                            return (
                              <div
                                key={channel.id}
                                className={`flex items-center justify-between px-3 py-2.5 rounded-md mb-1 transition-all ${
                                  isSelected
                                    ? 'bg-white/10 border border-white/20'
                                    : 'hover:bg-white/5 border border-transparent'
                                }`}
                              >
                                <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    value={channel.id}
                                    checked={isSelected}
                                    onChange={handleToggleChannel}
                                    disabled={!channel.is_member}
                                    className="w-4 h-4 rounded accent-black disabled:opacity-50 disabled:cursor-not-allowed"
                                  />
                                  <span className={`text-sm font-medium ${
                                    channel.is_member
                                      ? 'text-white'
                                      : 'text-white/40'
                                  }`}>
                                    #{channel.name}
                                  </span>
                                </label>

                                {/* Invite/Invited Button */}
                                {channel.is_member ? (
                                  <div className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs bg-white/10 border border-white/20 text-white/80">
                                    <CheckCircle className="w-3 h-3" />
                                    Invited
                                  </div>
                                ) : (
                                  <Button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInviteToChannel(channel.id);
                                    }}
                                    disabled={isInviting}
                                    variant="ghost"
                                    size="sm"
                                    className="h-auto gap-1.5 rounded-md px-2.5 py-1 text-xs bg-white/10 border border-white/20 text-white hover:bg-white/15 hover:border-white/30"
                                  >
                                    {isInviting ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Inviting...
                                      </>
                                    ) : (
                                      <>
                                        <UserPlus className="w-3 h-3" />
                                        Invite
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-sm text-white/60 py-8 text-center">
                            No channels found matching "{searchQuery}"
                          </p>
                        )}
                      </div>
                    </ScrollArea>

                    {/* Channel count info */}
                    <p className="text-xs text-white/60">
                      {selectedChannel.length} selected • {sortedChannels.filter(ch => ch.is_member).length} of {channels.length} channels available
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-white/60 py-4 text-center rounded-md border-[0.5px] border-white/10 bg-white/[0.08]">
                    No channels found
                  </p>
                )}
              </div>
            )}

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-sm text-white">Message (Optional)</Label>
              <TextArea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message to accompany the report"
                rows={3}
                className="w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-white placeholder:text-white/40 backdrop-blur-sm hover:bg-white/10 focus-visible:ring-white/20 resize-none"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="button"
              onClick={handleSendToSlack}
              disabled={!canSend || isSending}
              variant="white"
              className="w-full rounded-md bg-white px-3 pb-3 pt-[11px] text-center text-sm font-medium text-[#181818] hover:opacity-90"
            >
              {isSending
                ? 'Sending to Slack...'
                : selectedChannel.length > 1
                  ? `Send to ${selectedChannel.length} Channels`
                  : 'Send to Slack'
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
